import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import DynamicFormControl from '@/components/DynamicFormControl';

import {
  buildDataProviderFieldPath,
  useCategoriesBySourceType
} from '../CloudSourceHooks/useCategoriesBySourceType.hooks';
import DynamicFormField from '../DynamicFormField';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import SOURCE_TYPE, { DataProviderField } from '../SourceCreate.constants';
import type {
  CategoryFieldProps,
  DynamicFieldData,
  SourceCreateDynamicFormModel
} from '../SourceCreate.types';
import { getCategoryFilterSuffix } from '../SourceCreate.utils';

/**
 * Checks if default categories should be applied to the field.
 *
 * BUSINESS RULE:
 * Pre-select default categories only when:
 * 1. User hasn't manually selected categories yet (initialValue is empty)
 * 2. Form field is also empty (currentValue is empty)
 * 3. We have defaults to apply (defaultSelectedCategories is non-empty)
 *
 * Why: In "New" mode, we want to help users by pre-selecting common categories.
 * But we never overwrite user selections or pre-populated Edit mode values.
 *
 * @returns true if defaults should be applied
 */
const shouldApplyDefaults = (
  initialValue: unknown,
  currentValue: unknown,
  defaultSelectedCategories: string[]
): boolean => {
  // User has already selected categories - don't overwrite
  if (!isEmpty(initialValue)) {
    return false;
  }

  // Form already has categories - don't overwrite
  if (!isEmpty(currentValue)) {
    return false;
  }

  // No defaults available to apply
  if (defaultSelectedCategories.length === 0) {
    return false;
  }

  return true;
};

const CategoryField = ({
  provider,
  subProvider,
  sourceTypeId,
  field,
  initialValue,
  mode
}: CategoryFieldProps) => {
  const { formState, setValue, getValues } =
    useFormContext<SourceCreateDynamicFormModel>();
  const { errors } = formState;

  // Determine which category suffix to filter by (for Entra ID sub-providers)
  const categoryFilterSuffix = getCategoryFilterSuffix(subProvider);

  const { categoryNames, defaultSelectedCategories } =
    useCategoriesBySourceType(
      sourceTypeId,
      categoryFilterSuffix,
      subProvider,
      mode
    );

  // Apply default category selection when appropriate
  useEffect(() => {
    const currentValue = getValues(
      field.name as keyof SourceCreateDynamicFormModel
    );

    if (
      shouldApplyDefaults(initialValue, currentValue, defaultSelectedCategories)
    ) {
      setValue(
        field.name as keyof SourceCreateDynamicFormModel,
        [...defaultSelectedCategories],
        { shouldValidate: false, shouldDirty: false }
      );
    }
  }, [
    defaultSelectedCategories,
    field.name,
    initialValue,
    setValue,
    getValues
  ]);

  // Update field with current form state and available options
  const currentValue = getValues(
    field.name as keyof SourceCreateDynamicFormModel
  );
  // Normalize empty object to null for type compatibility
  let normalizedValue: string | string[] | null = null;
  if (currentValue && typeof currentValue === 'object') {
    if (Array.isArray(currentValue)) {
      normalizedValue = currentValue as string[];
    } else if (Object.keys(currentValue).length > 0) {
      normalizedValue = currentValue as string[];
    }
  } else if (typeof currentValue === 'string') {
    normalizedValue = currentValue;
  }

  const updatedField: DynamicFieldData = {
    ...field,
    options: categoryNames,
    showSelectAll:
      sourceTypeId !== SOURCE_TYPE.O365 &&
      sourceTypeId !== SOURCE_TYPE.MICROSOFT_DEFENDER &&
      sourceTypeId !== SOURCE_TYPE.MICROSOFT_ENTRA_ID &&
      sourceTypeId !== SOURCE_TYPE.MICROSOFT,
    value: normalizedValue || field.value
  };

  return (
    <DynamicFormField
      key={buildDataProviderFieldPath(
        provider,
        DataProviderField.DataproviderCategory
      )}
      formField={updatedField}
      xs={12}
      sm={6}
      md={6}
    >
      <DynamicFormControl {...updatedField} name={updatedField.name} />
      {get(errors, updatedField.name) && (
        <ErrorMessage message={get(errors, updatedField.name)?.message} />
      )}
    </DynamicFormField>
  );
};

export default CategoryField;
